import { useState } from 'react';
import { Button, List, Popconfirm, Space, Switch, Tag, Typography, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import {
  deleteAutoSync,
  listAutoSyncs,
  runAutoSync,
  updateAutoSync,
  type SheetAutoSync,
} from '@/api/submissions';

const { Text, Link } = Typography;

export const autoSyncQueryKey = (communityId: number) => ['sheet-auto-syncs', communityId];

/** Trạng thái hiển thị — luôn có chữ, không chỉ dựa vào màu. */
export function autoSyncState(item: SheetAutoSync, today = dayjs()): { color: string; label: string } {
  if (dayjs(item.endDate).isBefore(today, 'day')) return { color: 'default', label: 'Đã hết hạn' };
  if (!item.enabled) {
    return item.consecutiveFailures >= 5
      ? { color: 'error', label: 'Tạm dừng do lỗi' }
      : { color: 'default', label: 'Đã tắt' };
  }
  if (item.lastStatus === 'ERROR') return { color: 'error', label: 'Lỗi lần gần nhất' };
  if (item.lastStatus === 'OK') return { color: 'success', label: 'Đang chạy' };
  if (item.lastStatus === 'SKIPPED') return { color: 'success', label: 'Đang chạy · không có gì mới' };
  return { color: 'processing', label: 'Chờ lần chạy đầu' };
}

function filterSummary(item: SheetAutoSync): string {
  const parts: string[] = [];
  if (item.cohorts?.length) parts.push(`Khóa ${item.cohorts.join(', ')}`);
  if (item.assignmentIds?.length) parts.push(`${item.assignmentIds.length} bài tập`);
  else if (item.courseId) parts.push('1 khóa học');
  if (item.dueDateFrom || item.dueDateTo) parts.push('lọc hạn nộp');
  if (item.studentSearch) parts.push(`tìm "${item.studentSearch}"`);
  return parts.length ? parts.join(' · ') : 'Toàn bộ bài làm của cộng đồng';
}

/**
 * Danh sách đồng bộ tự động của một cộng đồng: bật/tắt, chạy ngay, xoá,
 * xem kết quả lần chạy gần nhất.
 */
export function AutoSyncList({ communityId }: { communityId: number }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: autoSyncQueryKey(communityId),
    queryFn: () => listAutoSyncs(communityId),
  });
  const [busyId, setBusyId] = useState<number | null>(null);

  const act = async (id: number, action: () => Promise<unknown>, success: string) => {
    setBusyId(id);
    try {
      await action();
      message.success(success);
      await queryClient.invalidateQueries({ queryKey: autoSyncQueryKey(communityId) });
    } catch (err) {
      message.error(extractApiErrorMessage(err, 'Không thực hiện được, thử lại sau.'));
    } finally {
      setBusyId(null);
    }
  };

  if (!query.data?.length) return null;

  return (
    <List
      size="small"
      header={<Text strong>Đồng bộ tự động của cộng đồng này (30 phút/lần, từ 00h đến 21h)</Text>}
      dataSource={query.data}
      style={{ marginTop: 16 }}
      renderItem={(item) => {
        const state = autoSyncState(item);
        const busy = busyId === item.id;
        return (
          <List.Item>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space wrap size={8} style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space wrap size={6}>
                  <Tag color={state.color}>{state.label}</Tag>
                  <Text>{filterSummary(item)}</Text>
                </Space>
                <Space wrap size={8}>
                  <Switch
                    size="small"
                    checked={item.enabled}
                    loading={busy}
                    aria-label={item.enabled ? 'Tắt tự động đồng bộ' : 'Bật tự động đồng bộ'}
                    onChange={(checked) =>
                      act(item.id, () => updateAutoSync(item.id, { enabled: checked }),
                        checked ? 'Đã bật lại tự động đồng bộ' : 'Đã tắt tự động đồng bộ')
                    }
                  />
                  <Button
                    size="small"
                    loading={busy}
                    onClick={() => act(item.id, () => runAutoSync(item.id), 'Đã chạy xong, xem kết quả bên dưới')}
                  >
                    Chạy ngay
                  </Button>
                  <Popconfirm
                    title="Xoá đồng bộ tự động này?"
                    description="Sheet giữ nguyên dữ liệu hiện có, chỉ ngừng cập nhật."
                    okText="Xoá"
                    cancelText="Huỷ"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => act(item.id, () => deleteAutoSync(item.id), 'Đã xoá đồng bộ tự động')}
                  >
                    <Button size="small" danger disabled={busy}>
                      Xoá
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Dừng sau ngày {dayjs(item.endDate).format('DD/MM/YYYY')}
                {' · '}
                {item.lastRunAt
                  ? `Chạy lần cuối ${dayjs(item.lastRunAt).format('HH:mm DD/MM')}`
                  : 'Chưa chạy lần nào'}
                {' · '}
                <Link href={item.sheetUrl} target="_blank" rel="noreferrer">
                  Mở sheet
                </Link>
              </Text>
              {item.lastMessage ? (
                <Text type={item.lastStatus === 'ERROR' ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                  {item.lastMessage}
                </Text>
              ) : null}
            </Space>
          </List.Item>
        );
      }}
    />
  );
}
